import { useEffect, useRef, useState } from 'react';

// Minimum elapsed time (seconds) and minimum progress delta required before
// we trust the rate enough to show an ETA. Keeps the early estimate from
// jumping wildly during the first few percent of progress.
const MIN_ELAPSED_SECONDS = 5;
const MIN_PROGRESS_DELTA = 1;

// Smoothing constant for exponential moving average of the rate.
// New readings get 30% weight; the smoothed history retains 70%.
const EMA_ALPHA = 0.3;

const formatTimeRemaining = (seconds) => {
  if (!seconds || seconds <= 0 || !isFinite(seconds)) return null;
  if (seconds > 3600) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `~${h}h ${m}m remaining`;
  }
  if (seconds > 60) {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `~${m}:${s.toString().padStart(2, '0')} remaining`;
  }
  return `~${Math.floor(seconds)}s remaining`;
};

/**
 * Tracks progress over time and returns a human-readable ETA string.
 *
 * Uses performance.now() for a monotonic clock so wall-clock changes (NTP
 * adjustments, daylight savings, sleep/wake) don't poison the rate calculation.
 * The shape of the rate math (progressDelta / elapsedSeconds) mirrors how the
 * backend bills processing time, keeping user-facing estimates and the actual
 * billed duration consistent.
 *
 * @param {number|undefined} progress - Current progress in [0, 100].
 * @param {boolean} isActive - Whether the tracked operation is currently running.
 * @returns {string|null} formatted ETA string, or null when unknown.
 */
const useEtaTracker = (progress, isActive) => {
  const startRef = useRef(null);
  const lastProgressRef = useRef(0);
  const smoothedRateRef = useRef(null);
  const prevRef = useRef(null);
  const [eta, setEta] = useState(null);

  useEffect(() => {
    if (!isActive || !progress || progress <= 0) {
      startRef.current = null;
      lastProgressRef.current = 0;
      smoothedRateRef.current = null;
      prevRef.current = null;
      setEta(null);
      return;
    }

    if (progress === lastProgressRef.current) return;

    if (!startRef.current || progress < lastProgressRef.current) {
      // First progress update or progress reset — record start time and reset EMA.
      startRef.current = { time: performance.now(), progress };
      smoothedRateRef.current = null;
      prevRef.current = null;
    }
    lastProgressRef.current = progress;

    const now = performance.now();
    const startTime = startRef.current?.time ?? now;
    const startProgress = startRef.current?.progress ?? progress;
    const elapsed = Math.max(0, (now - startTime) / 1000);
    const progressMade = Math.max(0, progress - startProgress);

    // Calculate instantaneous rate from delta since last update
    if (prevRef.current) {
      const dt = Math.max(0, (now - prevRef.current.time) / 1000);
      const dp = Math.max(0, progress - prevRef.current.progress);
      if (dt > 0 && dp > 0) {
        const instantaneousRate = dp / dt;
        if (smoothedRateRef.current === null) {
          smoothedRateRef.current = instantaneousRate;
        } else {
          smoothedRateRef.current =
            EMA_ALPHA * instantaneousRate + (1 - EMA_ALPHA) * smoothedRateRef.current;
        }
      }
    }
    prevRef.current = { time: now, progress };

    // Only show ETA after minimum thresholds are met (overall elapsed/progress)
    if (elapsed > MIN_ELAPSED_SECONDS && progressMade > MIN_PROGRESS_DELTA && smoothedRateRef.current > 0) {
      const remaining = Math.max(0, (100 - progress) / smoothedRateRef.current);
      setEta(formatTimeRemaining(remaining));
    }
  }, [progress, isActive]);

  return eta;
};

export { formatTimeRemaining };
export default useEtaTracker;
